// src/components/handover/SignaturePad.jsx
import React, { useEffect, useRef, useState } from 'react';

export default function SignaturePad({
  onChange,
  height = 160,
  disabled = false,
}) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const [width, setWidth] = useState(600);
  const isDrawing = useRef(false);
  const lastPoint = useRef({ x: 0, y: 0 });

  // Measure container width once and on resize
  useEffect(() => {
    function updateSize() {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      // Full width, but never stupidly huge
      const w = Math.max(260, Math.min(rect.width, 1200));
      setWidth(w);
    }

    updateSize();
    window.addEventListener('resize', updateSize);
    window.addEventListener('orientationchange', updateSize);

    return () => {
      window.removeEventListener('resize', updateSize);
      window.removeEventListener('orientationchange', updateSize);
    };
  }, []);

  // Set up canvas resolution & drawing style
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr =
      typeof window !== 'undefined'
        ? window.devicePixelRatio || 1
        : 1;

    canvas.width = width * dpr;
    canvas.height = height * dpr;

    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827'; // dark gray/near black
  }, [width, height]);

  function getPoint(e) {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();

    if (e.touches && e.touches.length > 0) {
      const t = e.touches[0];
      return {
        x: t.clientX - rect.left,
        y: t.clientY - rect.top,
      };
    }

    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }

  function handleStart(e) {
    if (disabled) return;
    e.preventDefault();
    const pt = getPoint(e);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    isDrawing.current = true;
    lastPoint.current = pt;
    ctx.beginPath();
    ctx.moveTo(pt.x, pt.y);
  }

  function handleMove(e) {
    if (!isDrawing.current || disabled) return;
    e.preventDefault();
    const pt = getPoint(e);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    ctx.lineTo(pt.x, pt.y);
    ctx.stroke();
    lastPoint.current = pt;
  }

  function handleEnd(e) {
    if (!isDrawing.current || disabled) return;
    e.preventDefault();
    isDrawing.current = false;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    if (onChange) onChange(dataUrl);
  }

  function clear() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (onChange) onChange('');
  }

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        maxWidth: '100%',
        borderRadius: 12,
        border: '1px solid #d1d5db',
        background: '#ffffff',
        padding: 8,
        boxSizing: 'border-box',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height,
          display: 'block',
          touchAction: 'none',
          borderRadius: 8,
        }}
        onMouseDown={handleStart}
        onMouseMove={handleMove}
        onMouseUp={handleEnd}
        onMouseLeave={handleEnd}
        onTouchStart={handleStart}
        onTouchMove={handleMove}
        onTouchEnd={handleEnd}
      />
      {!disabled && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: 6,
          }}
        >
          <button
            type="button"
            onClick={clear}
            style={{
              fontSize: 12,
              borderRadius: 999,
              border: '1px solid #d1d5db',
              padding: '3px 10px',
              background: '#f9fafb',
              cursor: 'pointer',
            }}
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
}
